import { pushToast } from '../components/Toast.js';

/**
 * Global API failure → toast. Both the SDK and the app's direct fetches go through
 * `window.fetch`, so wrapping it once surfaces EVERY API failure (403/429/5xx)
 * that callers would otherwise swallow — e.g. trying to mutate during a read-only
 * support session returns 403 and used to fail silently.
 *
 * Scope is deliberate: we toast on "you can't do that" (403), "slow down" (429),
 * and "the server broke" (5xx), plus network failures. We do NOT toast 400/401/
 * 402/404/409/422 — those are control-flow or handled inline by forms (validation,
 * auth redirect, the AI cap, expected-not-found, the upload replace prompt), so a
 * global toast would be noise or a double-message.
 */
export const TOAST_STATUSES = new Set([403, 408, 429, 500, 502, 503, 504]);

/** Build a user-facing toast from a status + the server's problem title/detail. */
export function messageForStatus(
  status: number,
  serverMessage: string,
): { title: string; message: string } {
  const m = serverMessage.trim();
  if (status === 403) {
    return { title: 'Not allowed', message: m || 'You don’t have permission to do that.' };
  }
  if (status === 429) {
    return {
      title: 'Too many requests',
      message: m || 'You’re going a bit fast — wait a moment and try again.',
    };
  }
  if (status === 408 || status === 504) {
    return { title: 'Timed out', message: m || 'That took too long. Please try again.' };
  }
  if (status >= 500) {
    return {
      title: 'Something went wrong',
      message: m || 'The server had a problem. Please try again in a moment.',
    };
  }
  return { title: 'Request failed', message: m || `Request failed (${status}).` };
}

// Suppress repeat toasts for the same message within a short window (a burst of
// failing calls shouldn't stack identical toasts).
const recent = new Map<string, number>();
const DEDUPE_MS = 4000;

export function shouldSuppress(key: string, now: number): boolean {
  const last = recent.get(key);
  recent.set(key, now);
  if (recent.size > 50) {
    for (const [k, ts] of recent) if (now - ts > 10_000) recent.delete(k);
  }
  return last !== undefined && now - last < DEDUPE_MS;
}

/** Pull a human message out of an RFC-9457 problem (or any { title|detail }) body. */
function problemMessage(body: unknown): string {
  if (body && typeof body === 'object') {
    const b = body as { detail?: unknown; title?: unknown };
    if (typeof b.detail === 'string' && b.detail.trim()) return b.detail;
    if (typeof b.title === 'string' && b.title.trim()) return b.title;
  }
  return '';
}

function urlOf(input: RequestInfo | URL): string {
  try {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return (input as Request).url ?? '';
  } catch {
    return '';
  }
}

/**
 * Wrap `window.fetch` once so API failures raise a toast. Idempotent (StrictMode /
 * re-imports won't double-wrap). The original response/rejection is always passed
 * through unchanged — this only observes.
 */
export function installApiErrorToasts(apiBaseUrl: string): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const w = window as Window & { __csFetchToastsInstalled?: boolean };
  if (w.__csFetchToastsInstalled) return;
  w.__csFetchToastsInstalled = true;

  let apiOrigin: string | null = null;
  try {
    apiOrigin = new URL(apiBaseUrl, window.location.href).origin;
  } catch {
    apiOrigin = null;
  }
  const isApiUrl = (url: string) =>
    Boolean(url) && (apiOrigin ? url.startsWith(apiOrigin) : url.startsWith(apiBaseUrl));

  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const isApi = isApiUrl(urlOf(input));
    try {
      const res = await original(input, init);
      if (isApi && TOAST_STATUSES.has(res.status)) {
        let serverMessage = '';
        try {
          serverMessage = problemMessage(await res.clone().json());
        } catch {
          // non-JSON error body — fall back to a status-based message
        }
        const { title, message } = messageForStatus(res.status, serverMessage);
        if (!shouldSuppress(`${res.status}:${message}`, Date.now())) {
          pushToast(message, { tone: 'error', title });
        }
      }
      return res;
    } catch (err) {
      // Network-level failure (offline / DNS / CORS) — only for API calls.
      if (isApi && !shouldSuppress('network', Date.now())) {
        pushToast('Couldn’t reach the server — check your connection and try again.', {
          tone: 'error',
          title: 'Network error',
        });
      }
      throw err;
    }
  };
}
